import { useMemo } from "react";
import { DataTable, Page } from "@iq/models";
import { FiRefreshCw } from "react-icons/fi";

const INCIDENTS_TABLE_CONFIG = {
    columns: [
        {
            key: "incidentId",
            label: "Incident #",
            accessor: "incidentId",
            filterable: false,
            width: ".75fr",
        },
        {
            key: "title",
            label: "Title",
            accessor: "title",
            filterable: true,
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "1.5fr",
        },
        {
            key: "clientID",
            label: "Client",
            accessor: "clientID",
            filterable: true,
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "1fr",
        },
        {
            key: "urgency",
            label: "Urgency",
            accessor: "urgency",
            filterable: true,
			quickValues: ["Critical", "High", "Medium", "Low"],
            width: ".75fr",
        },
        {
            key: "status",
            label: "Status",
            accessor: "status",
            filterable: true,
			quickValues: ["New", "Triaged", "In Progress", "Resolved", "Closed", "Cancelled"],
            width: "1fr",
        },
        {
            key: "product",
            label: "Product",
            accessor: "product",
            filterable: false,
            width: "1fr",
        },
        {
            key: "assignedTo",
            label: "Assigned To",
            accessor: "assignedTo",
            filterable: true,
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "1fr",
        },
    ],
};

function Incidents({
    incidents = [],
    clients = [],
    teamMembers = [],
    products = [],
    loadingIncidents = false,
    incidentsError = "",
    loadingClients = false,
    clientsError = "",
    loadingTeamMembers = false,
    teamMembersError = "",
    onIncidentsRefresh,
    onClientsRefresh,
    onTeamMembersRefresh,
    onIncidentAssign,
    assigningIncidentIds = {},
    onIncidentSelect,
}) {
    const productMap = useMemo(
        () =>
            new Map(
                (Array.isArray(products) ? products : []).map((product) => [
                    String(product?.id ?? ""),
                    product?.name || product?.product_name || `Product #${product?.id ?? "?"}`,
                ])
            ),
        [products]
    );

    const teamMemberMap = useMemo(
        () =>
            new Map(
                (Array.isArray(teamMembers) ? teamMembers : []).map((member) => [
                    String(member?.id ?? ""),
                    member?.name || member?.email || `User #${member?.id ?? "?"}`,
                ])
            ),
        [teamMembers]
    );

    const clientMap = useMemo(
        () =>
            new Map(
                (Array.isArray(clients) ? clients : []).map((client) => [
                    String(client?.id ?? ""),
                    client?.name || `Client #${client?.id ?? "?"}`,
                ])
            ),
        [clients]
    );

    const assigneeOptions = useMemo(
        () =>
            (Array.isArray(teamMembers) ? teamMembers : [])
                .map((member) => ({
                    id: member?.id,
                    label: member?.name || member?.email || `User #${member?.id ?? "?"}`,
                }))
                .filter((option) => option.id),
        [teamMembers]
    );

    const hasRelatedLookupErrors = Boolean(clientsError || teamMembersError);
    const relatedLookupLoading = loadingClients || loadingTeamMembers;

    const headerActions = [
        {
            id: "refresh-incidents",
            label: "Refresh",
            color: "secondary",
            onClick: () => {
                onIncidentsRefresh?.();
                onClientsRefresh?.();
                onTeamMembersRefresh?.();
            },
            icon: <FiRefreshCw aria-hidden="true" />,
            disabled: loadingIncidents || relatedLookupLoading,
        },
    ];

    const tableRows = useMemo(
        () =>
            (Array.isArray(incidents) ? incidents : []).map((incident) => ({
                ...incident,
                incidentId: incident?.number || `INC-${incident?.id ?? ""}`,
                occuredAt: incident?.occurred_at || incident?.created_at || "N/A",
                title: incident?.title || "N/A",
                description: incident?.description || "N/A",
                clientID:
                    incident?.client_id != null && incident?.client_id !== ""
                        ? clientMap.get(String(incident.client_id)) || `Client #${incident.client_id}`
                        : "Unassigned",
                urgency: incident?.urgency || "N/A",
                status: incident?.status || "N/A",
                product:
                    incident?.product_id != null && incident?.product_id !== ""
                        ? productMap.get(String(incident.product_id)) || `Product #${incident.product_id}`
                        : "Unassigned",
                assignedTo:
                    incident?.assigned_to_id != null && incident?.assigned_to_id !== ""
                        ? teamMemberMap.get(String(incident.assigned_to_id)) || `User #${incident.assigned_to_id}`
                        : "Unassigned",
            })),
        [incidents, clientMap, productMap, teamMemberMap]
    );

    return (
        <Page>
            {incidentsError ? (
                <p style={{ color: "var(--color-danger)", marginBottom: 12 }}>{incidentsError}</p>
            ) : null}
            {hasRelatedLookupErrors ? (
                <p style={{ color: "var(--color-danger)", marginBottom: 12 }}>
                    {clientsError || teamMembersError}
                </p>
            ) : null}
            <DataTable
                rowData={tableRows}
                loading={loadingIncidents}
                tableConfig={INCIDENTS_TABLE_CONFIG}
                // headerActions={headerActions}
                onRowClick={(incident) => onIncidentSelect?.(incident)}
            />
        </Page>
    );
}

export default Incidents;